import type { PhysicsBall } from './engine/types';
import type { FunilCampaignSwatch } from './funilCampaignPalette';

type BallElWithThink = HTMLElement & { _funilLastThink?: boolean };

function tooltipStatus(state: string): string {
  switch (state) {
    case 'thinking':
      return 'IA respondendo';
    case 'sucked':
      return '→ humano';
    case 'descending':
      return '→ próxima etapa';
    default:
      return 'conversando';
  }
}

function escapeInitial(s: string): string {
  const c = s.slice(0, 1);
  if (!c) return '?';
  return c.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
}

/** Conteúdo HTML do indicador «IA a escrever» (três asteriscos). */
const TYPING_FACE_HTML =
  '<span class="funil-ia-ball-typing" aria-hidden="true">' +
  '<span class="funil-ia-ball-typing-star">*</span>' +
  '<span class="funil-ia-ball-typing-star">*</span>' +
  '<span class="funil-ia-ball-typing-star">*</span>' +
  '</span>';

/**
 * Atualiza o nó da bolinha sem passar pelo React (evita 60 re-renders/s → ecrã a piscar).
 * Mantém classes e estilos alinhados a `PhysicsBallComponent`.
 */
export function syncPhysicsBallDom(
  el: HTMLElement,
  ball: PhysicsBall,
  swatch: FunilCampaignSwatch | undefined,
): void {
  const isThinking = ball.visualState === 'thinking';
  const isSucked = ball.visualState === 'sucked';
  const isDescending = ball.visualState === 'descending';

  const classes = [
    'funil-ia-ball',
    `funil-ia-ball--${ball.lane}`,
    isThinking && 'funil-ia-ball--thinking',
    isSucked && 'funil-ia-ball--sucked',
    isDescending && 'funil-ia-ball--descending',
    swatch && 'funil-ia-ball--campaign',
  ].filter(Boolean) as string[];

  el.className = classes.join(' ');

  const scale = ball.scale * (isThinking ? 1.06 : 1);
  el.style.transform = `translate3d(${ball.x - ball.radius}px, ${ball.y - ball.radius}px, 0) scale(${scale})`;
  el.style.opacity = String(ball.opacity);

  if (swatch) {
    el.style.setProperty('--funil-ball-from', swatch.circleFrom);
    el.style.setProperty('--funil-ball-to', swatch.circleTo);
    el.style.setProperty('--funil-ball-shadow', swatch.circleShadow);
    el.style.setProperty('--funil-ball-thinking-glow', swatch.thinkingGlow);
  } else {
    el.style.removeProperty('--funil-ball-from');
    el.style.removeProperty('--funil-ball-to');
    el.style.removeProperty('--funil-ball-shadow');
    el.style.removeProperty('--funil-ball-thinking-glow');
  }

  const vs = ball.visualState;
  if (el.dataset.funilVs !== vs) {
    el.dataset.funilVs = vs;
    const tip = el.querySelector('.funil-ia-ball-tip');
    if (tip) {
      tip.textContent = `${ball.leadName} · ${tooltipStatus(vs)}`;
    }
    el.setAttribute('aria-label', `${ball.leadName} · ${tooltipStatus(vs)}`);
  }

  /**
   * Face (3 asteriscos) e anéis `::before/::after` — NÃO podem depender só de `visualState` mudar:
   * o motor pode reenviar `updateVisual({ thinking: true })` com `ball.visualState` ainda
   * `thinking`, e o `if` antigo nunca reexecutava a face → 2.ª+ mensagem sem * e anéis parados.
   * Reparamos o DOM se faltar `.funil-ia-ball-typing` / letra, e noutra entrada em «thinking»
   * removemos o mod `--thinking` um frame para o browser reiniciar a animação dos anéis.
   */
  const elH = el as BallElWithThink;
  const wasThinking = Boolean(elH._funilLastThink);

  const face = el.querySelector<HTMLElement>('.funil-ia-ball-face');
  let repairedTyping = false;
  if (face) {
    if (isThinking) {
      if (!face.querySelector('.funil-ia-ball-typing')) {
        face.innerHTML = TYPING_FACE_HTML;
        repairedTyping = true;
      }
    } else {
      if (face.querySelector('.funil-ia-ball-typing') || !face.querySelector('.funil-ia-ball-letter')) {
        face.innerHTML = `<span class="funil-ia-ball-letter">${escapeInitial(ball.initial)}</span>`;
      } else {
        const le = face.querySelector('.funil-ia-ball-letter');
        const want = escapeInitial(ball.initial);
        if (le && le.textContent !== want) {
          face.innerHTML = `<span class="funil-ia-ball-letter">${want}</span>`;
        }
      }
    }
  }

  const thinkEdgeIn = isThinking && !wasThinking;
  if (isThinking && (thinkEdgeIn || repairedTyping)) {
    el.classList.remove('funil-ia-ball--thinking');
    void (el as HTMLElement).offsetWidth;
    el.className = classes.join(' ');
  }
  elH._funilLastThink = isThinking;
}
